import React from 'react';
import { BellIcon, BellOffIcon } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { SettingsGroup, SettingsToggleRow } from '../../components/SettingsList';
import { Card, SectionTitle } from '../../components/ui/Card';
import { engagementConfig, recentCheckTimes, reminderTime, sendsAt, smartNotificationExamples, usualCheckTime } from '../../data/engagement';
import { useEngagement } from '../../contexts/EngagementContext';
import { useLocale } from '../../contexts/LocaleContext';
import type { ReminderFrequency } from '../../types/engagement';
const config = engagementConfig.smartReminders;
const FIXED_TIME = config.fallbackTime;

/** MOB/Settings/SmartReminders — adaptive timing, capped frequency. */
export function SmartReminders() {
  const {
    t,
    tl
  } = useLocale();
  const engagement = useEngagement();
  const usual = usualCheckTime(recentCheckTimes.slice(-config.learningSampleSize));
  const next = engagement.smartTiming ? reminderTime(usual) : FIXED_TIME;
  const {
    from,
    to
  } = config.defaultQuietHours;
  return <div className="px-2 pb-2" data-id="element-3976" data-mp="259hyeb78denl">
      <PageHeader title={t('eng.rem.title')} subtitle={t('eng.rem.subtitle')} showBack data-id="element-3977" data-mp="1r85iu0vzff28" />

      <Card tone="dark" className="rounded-panel p-3" data-id="element-3978" data-mp="1raia54nmr1ry">
        <p className="bee-overline text-[9px] font-bold text-ondark" data-id="element-3979" data-mp="1w8bia38hu5uv">{t('eng.rem.usual')}</p>
        <p className="bee-num mt-1 text-[40px] font-bold leading-none text-sand" data-id="element-3980" data-mp="2jxvyied9ip0g">{usual}</p>
        <p className="mt-1 text-[11.5px] text-ondark" data-id="element-3981" data-mp="2ow7b2wuxzdmd">
          {t('eng.rem.usualHint', {
          n: Math.min(config.learningSampleSize, recentCheckTimes.length)
        })}
        </p>
        <p className="bee-num mt-2 border-t border-sand/15 pt-2 text-[13px] font-medium text-gold" data-id="element-3982" data-mp="2f5cm3kqb9zx8">
          {engagement.smartTiming ? t('eng.rem.next', {
          time: next
        }) : t('eng.rem.fixedNext', {
          time: next
        })}
        </p>
      </Card>

      <div className="mt-2" data-id="element-3983" data-mp="37o8gyj64w45a">
        <SettingsGroup data-id="element-3984" data-mp="3hixk12jiy6ju">
          <SettingsToggleRow label={t('eng.rem.smart')} hint={t('eng.rem.smartHint', {
          time: FIXED_TIME
        })} checked={engagement.smartTiming} onChange={engagement.setSmartTiming} emphasis data-id="element-3985" data-mp="3hhcl70q6g1g6" />
          <SettingsToggleRow label={t('eng.rem.quiet')} hint={t('eng.rem.quietHint', {
          from,
          to
        })} checked={engagement.quietHours} onChange={engagement.setQuietHours} data-id="element-3986" data-mp="33imovnb6m5sv" />
        </SettingsGroup>
      </div>

      <section className="mt-3" data-id="element-3987" data-mp="2tribut52gv0o">
        <SectionTitle data-id="element-3988" data-mp="2otqcxf6088mz">{t('eng.rem.frequency')}</SectionTitle>
        <SegmentedTabs<ReminderFrequency> label={t('eng.rem.frequency')} value={engagement.frequency} onChange={engagement.setFrequency} tabs={[{
        id: 'essential',
        label: t('eng.rem.freq.essential')
      }, {
        id: 'balanced',
        label: t('eng.rem.freq.balanced')
      }, {
        id: 'all',
        label: t('eng.rem.freq.all')
      }]} data-id="element-3989" data-mp="2ou01cybei7qk" />
        <p className="mt-2 px-1 text-[12px] leading-snug text-deep" data-id="element-3990" data-mp="qbn5dxwggegh">
          {t(`eng.rem.freqHint.${engagement.frequency}`)}{' '}
          <span className="bee-num text-muted" data-id="element-3991" data-mp="zcl8mdz2khy0">
            {t('eng.rem.cap', {
            n: config.dailyCap[engagement.frequency]
          })}
          </span>
        </p>
      </section>

      <section className="mt-3" data-id="element-3992" data-mp="14awigopd6sg8">
        <SectionTitle data-id="element-3993" data-mp="7loxbhsnqlcu">{t('eng.rem.examples')}</SectionTitle>
        <ul className="space-y-2" data-id="element-3994" data-mp="7n9tfbumecct">
          {smartNotificationExamples.map(example => {
          const sent = sendsAt(example, engagement.frequency);
          const health = example.kind === 'dailyCheck';
          return <li key={example.id} className={`rounded-card border p-2 ${health ? 'border-gold/45 bg-white' : 'border-sand-line bg-white/70'} ${sent ? '' : 'opacity-60'}`} data-id="element-3995" data-mp="lcjye7wwzy5v">
                <div className="flex items-center justify-between gap-2" data-id="element-3996" data-mp="3p0c3f31wjmxy">
                  <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-3997" data-mp="1vk9649mf8vag">
                    {t(`eng.rem.kind.${example.kind}`)}
                  </span>
                  <span className={`flex items-center gap-1 text-[10.5px] font-medium ${sent ? 'text-soft' : 'text-muted'}`} data-id="element-3998" data-mp="24b9hv0mjxqkj">
                    {sent ? <BellIcon size={11} aria-hidden="true" data-id="element-3999" data-mp="2e8wbkt8ifmok" /> : <BellOffIcon size={11} aria-hidden="true" data-id="element-4000" data-mp="2e7uotri4tbr0" />}
                    {sent ? t('eng.rem.sent') : t('eng.rem.notSent')}
                  </span>
                </div>
                <p className={`mt-1 text-[13.5px] text-deep ${health ? 'font-bold' : 'font-medium'}`} data-id="element-4001" data-mp="2n92n49knecl3">
                  {tl(example.title)}
                </p>
                <p className="text-[12px] text-muted" data-id="element-4002" data-mp="sw2xdnmk0203">{tl(example.body)}</p>
                <p className="mt-1 text-[11px] text-soft" data-id="element-4003" data-mp="sy5xx1lc1dj2">{tl(example.timing)}</p>
              </li>;
        })}
        </ul>
      </section>

      <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-4004" data-mp="ervnmho93onj">{t('eng.rem.note')}</p>
    </div>;
}